const cleanText = value =>
  String(value || "")
    .replace(/"/g, "")
    .trim()

const getBrandCarsDisplayOrder = cars => {
  const regularCars = []
  const commercialCars = []

  cars.forEach(car => (car.kind?.includes("commercial") ? commercialCars : regularCars).push(car))

  return [...regularCars, ...commercialCars]
}

const getCarRoute = car => {
  if (!car || !["new", "pre-owned"].includes(car.sellingStatus) || !car.slug || !car.brandImageUrlPath) return null
  const parts = car.slug.split("/").filter(Boolean)
  const [model, version] = parts.slice(-2)
  if (!model || !version) return null

  return {
    page: "version",
    params: { brand: car.brandImageUrlPath, model, version }
  }
}

const MAIN_MENU_COLLECTION_PATHS = new Set([
  "/es/renting",
  "/es/renting-seminuevos",
  "/es/renting-furgonetas",
  "/es/renting-particulares",
  "/es/renting-autonomos",
  "/es/renting-empresas",
  "/es/renting/suv",
  "/es/renting/opcion-compra",
  "/es/renting/100-euros",
  "/es/renting/150-euros",
  "/es/renting/200-euros"
])

const isMainMenuCollectionPath = value => {
  const path = String(value || "").split("?")[0].replace(/\/$/, "")
  return MAIN_MENU_COLLECTION_PATHS.has(path)
}

const getSeoPageCars = seoData =>
  (Array.isArray(seoData?.page_sections) ? seoData.page_sections : [])
    .reduce((cars, section) => (
      Array.isArray(section?.content?.cars)
        ? [...cars, ...section.content.cars]
        : cars
    ), [])

const buildImages = (car, cloudinaryBase = "") => {
  let paths = []

  if (car.image) {
    paths = [car.image, car.imageRear, car.imageInterior]
  } else if (car.brandImageUrlPath && car.modelImageUrlPath) {
    paths = [
      `q_auto,f_auto/v1/car_model/${car.brandImageUrlPath}/${car.modelImageUrlPath}`,
      `v1/car_model/${car.brandImageUrlPath}/${car.modelImageUrlPath}_rear`,
      `v1/car_model/${car.brandImageUrlPath}/${car.modelImageUrlPath}_interior`
    ]
  }

  return paths.filter(Boolean).map(path => `${cloudinaryBase}${path}`)
}

const buildProductSchema = (car, { url, cloudinaryBase = "", aggregate = false } = {}) => {
  const price = Number(car && car.minimumPrice)
  if (!car || !url || !Number.isFinite(price) || price <= 0) return null

  const availability = car.sold ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
  const offer = {
    "@type": "Offer",
    url,
    price,
    priceCurrency: "EUR",
    availability,
    businessFunction: "http://purl.org/goodrelations/v1#LeaseOut",
    seller: { "@type": "Organization", name: "Swipcar" },
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price,
      priceCurrency: "EUR",
      unitCode: "MON"
    }
  }
  const ranges = Array.isArray(car.priceRanges) ? car.priceRanges : []
  const highPrice = Number(car.maximumPrice)
  const offers = aggregate
    ? {
        "@type": "AggregateOffer",
        lowPrice: price,
        highPrice: Number.isFinite(highPrice) && highPrice >= price ? highPrice : price,
        priceCurrency: "EUR",
        offerCount: ranges.length || 1,
        offers: [offer]
      }
    : offer
  const images = buildImages(car, cloudinaryBase)
  const version = cleanText(car.version)

  return {
    "@type": "Product",
    name: [car.brand, car.model, version].filter(Boolean).join(" "),
    description: `Renting ${[car.brand, car.model, version].filter(Boolean).join(" ")} desde ${price}€ al mes`,
    ...(images.length ? { image: images } : {}),
    url,
    brand: { "@type": "Brand", name: car.brand },
    ...(car.id ? { sku: String(car.id) } : {}),
    offers
  }
}

const buildCollectionSchema = (
  cars,
  { name, url, cloudinaryBase = "", getProductUrl }
) => {
  const itemListElement = cars
    .map(car => {
      const route = getCarRoute(car)
      if (!route || route.page !== "version") return null
      const product = buildProductSchema(car, {
        url: getProductUrl(route),
        cloudinaryBase
      })
      return product ? { "@type": "ListItem", item: product } : null
    })
    .filter(Boolean)
    .map((item, index) => ({ ...item, position: index + 1 }))

  if (!itemListElement.length) return null

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url,
    mainEntity: {
      "@type": "ItemList",
      name: `Ofertas de ${name}`,
      numberOfItems: itemListElement.length,
      itemListElement
    }
  }
}

const serializeJsonLd = value => JSON.stringify(value).replace(/</g, "\\u003c")

module.exports = {
  buildCollectionSchema,
  buildProductSchema,
  getBrandCarsDisplayOrder,
  getCarRoute,
  getSeoPageCars,
  isMainMenuCollectionPath,
  serializeJsonLd
}
